import { Alert, Box, Button, TextField, Typography } from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import React, { useEffect, useRef, useState } from "react";
import { AxiosError } from "axios";
import { finalize } from "rxjs";
import ModalContent from "src/Shared/Components/ModalContent/ModalContent";
import { useModal, useStore } from "src/Shared/Hooks";
import { IResult } from "src/Shared/Interfaces";
import { CommonService, ToastService } from "src/Shared/Services";
import { AddressParts, EMPTY_ADDRESS_PARTS, geocodeAddress } from "src/Shared/Tools/GooglePlaces";
import { ISiteRegionAutoDetect } from "src/Shared/Interfaces/ISiteRegionAutoDetect.interface";
import CompanyOfficeService from "../../Services/CompanyOffice.service";
import { ICompanyOffice } from "../../Interfaces";
import { parseOfficesCsv } from "../../Tools/OfficeCsvUtils";
import { resolveSiteRegion } from "../AddOrEditOffices/SiteRegion.rules";
import ImperansReferencePicker from "../ImperansReference/ImperansReferencePicker";
import { useImperansReferenceList } from "../ImperansReference/useImperansReferenceList";
import { clinicByName } from "../ImperansReference/ImperansReference.rules";
import { IImperansReferencePick } from "../../Interfaces/IImperansReference";

/**
 * What one geocode of an imported row settles: the TDDA region its coordinates fall inside (bug
 * 5826) and the address parts Imperans takes beside the formatted string (bug 6494). Both come from
 * the same lookup, so a row is still geocoded at most once.
 */
type ImportedSitePlacement = {
  franchiseID: number | null;
  parts: AddressParts;
};

type Props = {
  companyRowID: string;
  /** The client's country, which decides the Imperans clinic list (story 6373). */
  countryCode?: string | null;
  /** Wizard only: every imported site needs its Imperans clinic, from the column or the default. */
  requireServicingClinic?: boolean;
};

/**
 * Bulk site import (S3.7, story 5601): the same CSV shape as the downloadable template, accepted
 * either pasted or as an uploaded file. Rows group by an exact site name — the first row for a name
 * carries its sub code, address and region, later rows contribute points of contact.
 * A row with no site name is skipped without aborting; a row that fails the manual form's own rules
 * is reported rather than stored. Importing a name that already exists creates a second site — a
 * documented Imperans limitation, never a silent merge.
 */
const OfficeBulkImport: React.FC<Props> = ({
  companyRowID,
  countryCode,
  requireServicingClinic = false,
}) => {
  const modal = useModal();

  // The servicing clinic (Epic 6303, story 6373): a row's own Servicing Clinic column wins when it
  // names a clinic in the Imperans list; otherwise the default picked here applies.
  const clinicList = useImperansReferenceList("clinics", countryCode);
  const [defaultClinic, setDefaultClinic] = useState<IImperansReferencePick | null>(null);
  const {
    rootStore: {
      companyOfficeStore: { refreshCompanyOfficeList },
      companyStore: { getCompanyMenu },
    },
  } = useStore();

  const [text, setText] = useState<string>("");
  const [errors, setErrors] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [detectingRegions, setDetectingRegions] = useState<boolean>(false);
  const [regions, setRegions] = useState<any[]>([]);
  // How territories map to regions, and whether detection is on for each country — the same settings
  // the manual site form reads (S3.4, bug 5695), fetched once so every row in the import can use it.
  const [autoDetect, setAutoDetect] = useState<ISiteRegionAutoDetect | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    CommonService.getRegions().subscribe({
      next: (data: any) => setRegions(data || []),
      error: () => setRegions([]),
    });
    CommonService.getSiteRegionAutoDetect().subscribe({
      next: (data) => setAutoDetect(data || null),
      error: () => setAutoDetect(null),
    });
  }, []);

  const parsed = parseOfficesCsv(text);

  const onFileChosen = (file?: File) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setText(`${reader.result || ""}`);
    reader.readAsText(file);
  };

  /** A region name in the file is matched to the database-loaded region list (decision 16). */
  const resolveRegion = (regionName: string | null): number | null => {
    if (!regionName) return null;
    const match = regions.find(
      (region) =>
        `${region.label || ""}`.trim().toLowerCase() === regionName.trim().toLowerCase(),
    );
    return match ? Number(match.value) : null;
  };

  /**
   * A row whose Region column did not resolve — blank, or text matching no known region — is placed
   * in the same mapped TDDA territory the manual form would put it in, geocoding the address since an
   * imported row was never picked from the autocomplete dropdown and so carries no coordinates of its
   * own (bug 5826). An address the lookup cannot place is left with no region, exactly as the manual
   * form leaves it for an address outside every mapped territory — never guessed, never wrong.
   */
  const resolveRegionByAddress = async (address: string): Promise<ImportedSitePlacement> => {
    const details = await geocodeAddress(address);
    const outcome = await resolveSiteRegion(details, autoDetect);
    return {
      franchiseID: outcome.kind === "detected" ? outcome.franchiseID : null,
      // The one geocode this row gets also yields its address parts (bug 6494); no second call.
      parts: details ?? EMPTY_ADDRESS_PARTS,
    };
  };

  const onImport = async () => {
    setErrors(parsed.errors);
    if (parsed.errors.length > 0) return;

    if (parsed.rows.length === 0) {
      setErrors([
        "Nothing to import — check the file matches the template and keep the text to correct it.",
      ]);
      return;
    }

    // Each row's clinic: the column when it names a clinic Imperans offers, else the default. In the
    // wizard a row left with neither is reported rather than stored without one (story 6373).
    const clinics = parsed.rows.map(
      (row) =>
        (row.servicingClinicName && clinicByName(row.servicingClinicName, clinicList.options)) ||
        (defaultClinic?.id ? { id: defaultClinic.id, label: defaultClinic.name || "" } : null),
    );
    if (requireServicingClinic) {
      const missing = parsed.rows
        .filter((_, index) => !clinics[index])
        .map((row) =>
          row.servicingClinicName
            ? `"${row.officeName}": "${row.servicingClinicName}" is not a clinic Imperans offers — correct it or pick a default clinic.`
            : `"${row.officeName}": no servicing clinic — add one in the Servicing Clinic column or pick a default clinic.`,
        );
      if (missing.length) {
        setErrors(missing);
        return;
      }
    }

    setDetectingRegions(true);
    const placements = await Promise.all(
      parsed.rows.map((row) => {
        const fromColumn = resolveRegion(row.regionName);
        // A row whose Region column resolves is never geocoded, so it carries no parts and its
        // formatted string is authoritative on its own (story 6356).
        return fromColumn != null
          ? Promise.resolve<ImportedSitePlacement>({ franchiseID: fromColumn, parts: EMPTY_ADDRESS_PARTS })
          : resolveRegionByAddress(row.officeFullAddress);
      }),
    );
    setDetectingRegions(false);

    const request = parsed.rows.map(
      (row, index) =>
        ({
          companyRowID,
          officeName: row.officeName,
          subCode: row.subCode,
          officeFullAddress: row.officeFullAddress,
          streetAddress: placements[index].parts.line1,
          suburb: placements[index].parts.suburb,
          city: placements[index].parts.townCity,
          state: placements[index].parts.state,
          postCode: placements[index].parts.postCode,
          franchiseID: placements[index].franchiseID,
          imperansFranchiseOfficeID: clinics[index]?.id ?? null,
          imperansFranchiseOfficeName: clinics[index]?.label ?? null,
          pointsOfContact: row.pointsOfContact,
          // A bulk import never disturbs an existing primary office (S3.5).
          isPrimaryOffice: false,
        }) as ICompanyOffice,
    );

    setLoading(true);
    CompanyOfficeService.addCompanySite(request)
      .pipe(
        // Button spinner only — no app-level Backdrop over the whole viewport.
        finalize(() => {
          setLoading(false);
        }),
      )
      .subscribe({
        next: (data: IResult<string>) => {
          ToastService.success(
            `${request.length} ${request.length === 1 ? "site" : "sites"} imported.`,
          );
          setText("");
          modal.close();
          // Silent refresh: rows repaint in place on the page the user was on, and the sidebar
          // count updates without the full-page loader.
          refreshCompanyOfficeList(companyRowID, true);
          getCompanyMenu(companyRowID, false);
        },
        error: (error: AxiosError) => ToastService.axiosError(error),
      });
  };

  return (
    <ModalContent
      onSaveButtonClick={onImport}
      buttonText={
        detectingRegions
          ? "Detecting regions…"
          : `Import${parsed.rows.length ? ` (${parsed.rows.length})` : ""}`
      }
      title="Bulk Import Sites"
      btnLoading={loading || detectingRegions}
    >
      <Typography variant="body2" sx={{ mb: 1 }}>
        Paste rows from the template, or upload the file. Columns: Sub/Project Code, Office/Site
        Name, Address, Contact Name, Mobile, Email, Region, Servicing Clinic. Quote any value
        containing a comma. Repeat a site's name on later rows to add more points of contact. The
        Servicing Clinic column names a clinic in the Imperans list; a row without one takes the
        default below. A file still carrying the old visit-logistics columns imports fine.
      </Typography>

      <Box sx={{ mb: 2 }}>
        <ImperansReferencePicker
          label="Default servicing clinic"
          list={clinicList}
          hasCountry={Boolean(countryCode)}
          value={defaultClinic}
          onChange={setDefaultClinic}
          required={requireServicingClinic}
          helperText={
            requireServicingClinic
              ? "Applied to every row whose Servicing Clinic column is blank or unmatched."
              : "Optional — applied to rows without a Servicing Clinic column value."
          }
        />
      </Box>

      <Box sx={{ mb: 1 }}>
        <Button
          size="small"
          startIcon={<UploadFileIcon />}
          onClick={() => fileInput.current?.click()}
        >
          Upload CSV
        </Button>
        <input
          ref={fileInput}
          type="file"
          accept=".csv,text/csv"
          hidden
          onChange={(e) => onFileChosen(e.target.files?.[0])}
        />
      </Box>

      <TextField
        fullWidth
        multiline
        minRows={8}
        placeholder={'"AK-01","Head Office","1 Example Street, City, 1234","Jane","021 555 0100","jane@example.co.nz"'}
        value={text}
        onChange={(e) => setText(e.target.value)}
      />

      {parsed.skipped > 0 && (
        <Alert severity="info" sx={{ mt: 1 }}>
          {parsed.skipped} {parsed.skipped === 1 ? "row" : "rows"} without a site name will be
          skipped.
        </Alert>
      )}

      {errors.length > 0 && (
        <Alert severity="error" sx={{ mt: 1 }}>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {errors.map((error) => (
              <li key={error}>{error}</li>
            ))}
          </Box>
        </Alert>
      )}
    </ModalContent>
  );
};

export default OfficeBulkImport;
